import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Check, CircleDashedIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { BriefSection } from "@/types/types";
import { BRIEF_SECTIONS } from "@/constants/brief-sections";

interface BriefSidebarProps {
  /** Content sections of the active mode — guided's BRIEF_SECTIONS (default) or freeform's single doc. */
  briefSections?: BriefSection[];
  briefGroupTitle?: string;
  messageSections: BriefSection[];
  currentSectionId: string;
  className?: string;
  onSectionChange: (sectionId: string) => void;
  isSectionComplete: (sectionId: string) => boolean;
}

export function BriefSidebar({
  briefSections = BRIEF_SECTIONS,
  briefGroupTitle = "Brief Sections",
  messageSections,
  currentSectionId,
  className,
  onSectionChange,
  isSectionComplete,
}: BriefSidebarProps) {
  const briefProgressValue = briefSections.filter((s) =>
    isSectionComplete(s.id)
  ).length;

  const messageProgressValue = messageSections.filter((s) =>
    isSectionComplete(s.id)
  ).length;

  return (
    <div className={cn("w-full lg:w-64 shrink-0 border-r", className)}>
      {/* Brief Sections */}
      <div className="p-4 border-b">
        <div className="mb-2 flex flex-row justify-between items-center">
          <span className="font-medium text-sm">{briefGroupTitle}</span>
          <span className="text-xs font-mono">
            {briefProgressValue}/{briefSections.length}
          </span>
        </div>
        <Progress
          variant="segmented"
          segments={briefSections.length}
          className="h-1"
          value={(briefProgressValue / briefSections.length) * 100}
        />
      </div>
      <div className="flex flex-col gap-1 p-2">
        {briefSections.map((section, idx) => (
          <SectionButton
            key={section.id}
            section={section}
            displayIndex={idx + 1}
            isActive={section.id === currentSectionId}
            isComplete={isSectionComplete(section.id)}
            onClick={() => onSectionChange(section.id)}
          />
        ))}
      </div>

      {/* Messages Section */}
      <div className="p-4 border-y">
        <div className="mb-2 flex flex-row justify-between items-center">
          <span className="font-medium text-sm">Messages</span>
          <span className="text-xs font-mono">
            {messageProgressValue}/{messageSections.length}
          </span>
        </div>
        <Progress
          variant="segmented"
          segments={messageSections.length}
          className="h-1"
          value={
            messageSections.length > 0
              ? (messageProgressValue / messageSections.length) * 100
              : 0
          }
        />
      </div>
      <div className="flex flex-col gap-1 p-2">
        {messageSections.map((section, idx) => (
          <SectionButton
            key={section.id}
            section={section}
            displayIndex={idx + 1}
            isActive={section.id === currentSectionId}
            isComplete={isSectionComplete(section.id)}
            onClick={() => onSectionChange(section.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface SectionButtonProps {
  section: BriefSection;
  displayIndex: number;
  isActive: boolean;
  isComplete: boolean;
  onClick: () => void;
}

function SectionButton({
  section,
  displayIndex,
  isActive,
  isComplete,
  onClick,
}: SectionButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={cn(
        "w-full text-left h-8 p-2 text-sm cursor-pointer rounded-md flex gap-2.5 justify-start transition-none",
        isActive && "bg-muted font-medium",
        !isActive &&
          "hover:bg-muted/80 dark:hover:bg-sidebar text-foreground/80 font-normal"
      )}
    >
      <span className="shrink-0 pl-1 font-mono text-xs text-muted-foreground">{displayIndex}.</span>
      <div className="shrink-0">
        {isComplete ? (
          <Check className="size-4 text-success" />
        ) : (
          <CircleDashedIcon className="size-4" />
        )}
      </div>
      <span className="truncate" title={section.title}>{section.title}</span>
    </Button>
  );
}
